import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PaginatedItems } from '../models/types';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { Client } from '../models/client.model';
import { BaseService } from './_base.service';

interface SearchParamsType {
  query: string | number;
}

export interface SearchResponseType {
  clients: Client[];
}

interface CountsByRatingType {
  rating: number;
  count: number;
}

@Injectable({
  providedIn: 'root',
})
export class ClientsService extends BaseService {
  private clients$: BehaviorSubject<Client[]> = new BehaviorSubject([]);

  index(query, pageIndex, pageSize, sort?: string, ratingFilter?: number[]) {
    return this.http
      .get<PaginatedItems<Client>>(
        `${this.apiURL}/clients`,
        this.getParams({
          query,
          page: pageIndex,
          page_size: pageSize,
          sort,
          'rating[]': ratingFilter,
        })
      )
      .pipe(
        catchError((error: HttpResponse<any>) => {
          console.error(error);
          return throwError(() => error);
        }),
      );
  }

  search(params: SearchParamsType) {
    return this.http.get<SearchResponseType>(
      `${this.apiURL}/clients/search`,
      this.getParams({
        search: params.query,
      }),
    );
  }

  all(): Observable<HttpResponse<Client[]>> {
    return this.http.get<Client[]>(`${this.apiURL}/clients/all`, this.getParams());
  }

  create(data): Observable<Client> {
    return this.http
      .post<Client>(`${this.apiURL}/clients`, data, this.getParams())
      .pipe(
        catchError((error: HttpResponse<any>) => {
          console.error(error);
          return throwError(() => error);
        }),
        map((response: HttpResponse<Client>) => response.body));
  }

  update(id: number, body: Partial<Client>) {
    return this.http.put<Client>(`${this.apiURL}/clients/${id}`, { client: body }, this.getParams({}));
  }

  get(id: number): Observable<HttpResponse<Client>> {
    return this.http.get<Client>(`${this.apiURL}/clients/${id}`, this.getParams());
  }

  countsByRating(): Observable<HttpResponse<CountsByRatingType[]>> {
    return this.http.get<CountsByRatingType[]>(`${this.apiURL}/clients/counts_by_rating`, this.getParams())
      .pipe(
        catchError((error: HttpResponse<CountsByRatingType[]>) => {
          console.error(error);
          return throwError(() => error);
        }),
      );
  }

  get allClients(): Observable<Client[]> {
    return this.clients$.asObservable();
  }
}
